import { anyReallyAny } from "./any.tools";

/** All the accepted function types accepted for the debounce function and decorator */
type AcceptedReturnTypes = Promise<anyReallyAny | void> | (anyReallyAny | void);
type AcceptedFunctionType<Params extends anyReallyAny[], T extends AcceptedReturnTypes> = ((...args: Params) => T) | (() => T)

/** 
 * Function that encapsulate an other function or method into a debouncer
 * @param {object} params 
 * @param {Function} params.func - The function to debounce
 * @param {number} params.timeout - The timeout to set in ms
 * @param {boolean} params.useSamePromise - If the function given in params.func return a promise, tell if we return th same promise untill it's completed or if we recreate a new on each time - Default true
 * @returns the function encapsulated in a set timeout wrapper
 */
export function debounceFunction<Params extends anyReallyAny[], T extends AcceptedReturnTypes>({ func, timeout, useSamePromise = true }: {
    func: AcceptedFunctionType<Params, T>,
    timeout: number,
    useSamePromise?: boolean // If the func parameter return a promise, we keep the same promise until it's resolved
}): (...args: Params) => Promise<T> {

    let timer: ReturnType<typeof setTimeout>;// The timer of the wetTimeout wrapper
    let storedPromise: Promise<T> | undefined; // If the function return a promise, and we need to keep only one running, we store it here

    /** Return a function that encapsulate the function given in parameters */
    return function (this: anyReallyAny, ...args: Params) {

        const that = this; // We store the origin's context in order to pass it to our function 

        return new Promise<T>(function (resolve, reject) {
            // Timer clear for each call to prevent multiple unwanted call 
            clearTimeout(timer);
            timer = setTimeout(function () {
                try {

                    // Handle the useSamePromise param
                    if (useSamePromise) {

                        // Store the promise if there is no other running
                        if (storedPromise == null) {
                            storedPromise = Promise.resolve().then(() => func.call(that, ...args));
                            // it is ok to not catch error here since we resolve the promise just after
                            // eslint-disable-next-line promise/catch-or-return
                            storedPromise.then((value) => {
                                // Clear the stored promise at the end of it
                                storedPromise = undefined;
                                return value;
                            });
                        }
                        resolve(storedPromise);
                    } else {
                        const returnedValue = Promise.resolve().then(() => func.call(that, ...args));
                        resolve(returnedValue);
                    }
                } catch (e) {
                    reject(e);
                }
            }, timeout);
        });

    };
}

/**
 * Debounce decorator for class methods
 * @param timeout The timeout to set in ms
 * @param p The params objects
 * @param p.useSamePromise If the function given in params.func return a promise, tell if we return th same promise untill it's completed or if we recreate a new on each time - Default true
 */
export function Debounce(timeout: number, p?: { useSamePromise: boolean }) {

    return function (this: anyReallyAny, target: anyReallyAny, propertyKey: string, decriptor: PropertyDescriptor): PropertyDescriptor {

        const originalMethod = decriptor.value;
        const debounced = debounceFunction.call(this, {
            func: originalMethod,
            timeout: timeout,
            useSamePromise: p?.useSamePromise
        });
        decriptor.value = function (...args: anyReallyAny[]) {
            return debounced.apply(this, args);
        };


        return decriptor;
    };
}